import { useQuery } from "@tanstack/react-query";

import { supabase } from "~/integrations/supabase/client";

// Finished-product stock is the FÍSICO (bodega) balance in products.stock — the
// single source of truth. Production completion increments it (add_production_stock);
// DISPATCH (order -> in_transit/delivered) decrements it (handle_dispatch_stock_decrement).
// The detail view also surfaces "facturado no despachado" (get_reserved_not_dispatched)
// and "disponible para la venta" = físico − no despachado.

// get_reserved_not_dispatched was added after the last supabase types regeneration,
// so we call it through a thin typed wrapper (same pattern as facturaCostsService).
type RpcReader = <TRow>(
  fn: string,
  args: Record<string, unknown>
) => Promise<{ data: TRow[] | null; error: { message: string } | null }>;

const rpcRead = supabase.rpc.bind(supabase) as unknown as RpcReader;

export interface ProductStockListRow {
  product_id: string;
  name: string;
  sku: string | null;
  unit: string | null;
  group: string | null;
  stock: number;
}

const PRODUCT_STOCK_LIST_KEY = "product_stock_list";

export function useProductStockList() {
  return useQuery<ProductStockListRow[], Error>({
    queryKey: [PRODUCT_STOCK_LIST_KEY],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("products")
        .select(
          "id, sku, short_name, long_name, stock, base_measurement_unit, group_description"
        )
        .is("deleted_at", null)
        .order("stock", { ascending: false, nullsFirst: false });
      if (error) throw error;
      return (data ?? []).map((p) => ({
        product_id: p.id,
        name: p.long_name || p.short_name,
        sku: p.sku,
        unit: p.base_measurement_unit,
        group: p.group_description,
        stock: p.stock ?? 0,
      }));
    },
  });
}

const PRODUCT_RESERVED_KEY = "product_reserved_not_dispatched";

// Map of product_id -> "facturado no despachado" for all products (single call).
export function useReservedNotDispatched() {
  return useQuery<Map<string, number>, Error>({
    queryKey: [PRODUCT_RESERVED_KEY],
    queryFn: async () => {
      const { data, error } = await rpcRead<{
        product_id: string;
        reserved_qty: number;
      }>("get_reserved_not_dispatched", {});
      if (error) throw new Error(error.message);
      const map = new Map<string, number>();
      for (const row of data ?? [])
        map.set(row.product_id, Number(row.reserved_qty) || 0);
      return map;
    },
  });
}

export interface ProductStockDetailData {
  stock: number; // físico (bodega)
  reserved: number; // facturado no despachado
  available: number; // disponible para la venta = físico − no despachado
  unit: string | null;
  name: string;
  sku: string | null;
}

const PRODUCT_STOCK_DETAIL_KEY = "product_stock_detail";

export function useProductStockDetail(productId: string | null) {
  return useQuery<ProductStockDetailData | null, Error>({
    queryKey: [PRODUCT_STOCK_DETAIL_KEY, productId],
    enabled: !!productId,
    queryFn: async () => {
      const [productRes, reservedRes] = await Promise.all([
        supabase
          .from("products")
          .select("stock, base_measurement_unit, short_name, long_name, sku")
          .eq("id", productId as string)
          .single(),
        rpcRead<{ product_id: string; reserved_qty: number }>(
          "get_reserved_not_dispatched",
          { p_product_id: productId }
        ),
      ]);
      if (productRes.error) throw productRes.error;
      if (reservedRes.error) throw new Error(reservedRes.error.message);
      const stock = productRes.data.stock ?? 0;
      const reserved = Number(reservedRes.data?.[0]?.reserved_qty ?? 0);
      return {
        stock,
        reserved,
        available: stock - reserved,
        unit: productRes.data.base_measurement_unit,
        name: productRes.data.long_name || productRes.data.short_name,
        sku: productRes.data.sku,
      };
    },
  });
}

export interface ProductStockMovementRow {
  id: string;
  movement_type: string;
  quantity_change: number;
  reason: string | null;
  reference_type: string | null;
  created_at: string;
}

const PRODUCT_STOCK_MOVEMENTS_KEY = "product_stock_movements";

export function useProductStockMovements(productId: string | null) {
  return useQuery<ProductStockMovementRow[], Error>({
    queryKey: [PRODUCT_STOCK_MOVEMENTS_KEY, productId],
    enabled: !!productId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("stock_movements")
        .select(
          "id, movement_type, quantity_change, reason, reference_type, created_at"
        )
        .eq("product_id", productId as string)
        .order("created_at", { ascending: false })
        .limit(50);
      if (error) throw error;
      return (data ?? []).map((m) => ({
        id: m.id,
        movement_type: m.movement_type,
        quantity_change: Number(m.quantity_change) || 0,
        reason: m.reason,
        reference_type: m.reference_type,
        created_at: m.created_at ?? "",
      }));
    },
  });
}
